import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchInvoices, buildPendingFeatureInvoiceMap, type Invoice } from '@/store/slices/wallet';

/**
 * The active brand's OPEN (unpaid) feature-upgrade invoices, keyed by catalog item id — the
 * shared source of the "you have a payment to finish" signal that surfaces as the Product
 * sidebar dot, the Product-page card badges, and the home Spotlight upsell.
 *
 * Ensures the owner invoices are fetched (guarded, once per mount) so the signal is accurate
 * wherever this runs — including the always-mounted side rail, which may render before any
 * page that loads invoices. Reads `casinoInstance.displayBrand` so it scopes to the brand on
 * screen and updates the instant a payment is finished / cancelled (the invoice leaves the
 * pending set). See {@link buildPendingFeatureInvoiceMap}.
 */
export const usePendingFeatureInvoices = (): Map<number, Invoice> => {
  const dispatch = useAppDispatch();
  const invoices = useAppSelector((s) => s.ownerWallet.invoices);
  const invoicesLoaded = useAppSelector((s) => s.ownerWallet.invoicesLoaded);
  const invoicesLoading = useAppSelector((s) => s.ownerWallet.invoicesLoading);
  const activeBrandId = useAppSelector((s) => s.casinoInstance.displayBrand?.id);

  const requested = React.useRef(false);
  React.useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    if (!invoicesLoaded && !invoicesLoading) dispatch(fetchInvoices());
  }, [dispatch, invoicesLoaded, invoicesLoading]);

  return React.useMemo(
    () => buildPendingFeatureInvoiceMap(invoices, activeBrandId),
    [invoices, activeBrandId],
  );
};
